import { ticketingFetch } from "./client";

export type TicketAttachment = {
  id: number;
  ticket_id: number;
  uploaded_by: number;
  uploader_name: string | null;
  original_filename: string;
  declared_content_type: string;
  detected_content_type: string | null;
  size_bytes: number;
  checksum_sha256: string;
  created_at: string | null;
  status: string;
  is_internal: boolean;
};
export type AttachmentDownload = {
  blob: Blob;
  filename: string;
  contentType: string | null;
};

export class AttachmentApiError extends Error {
  status: number | null;
  errorCode: string | null;

  constructor(message: string, status: number | null, errorCode: string | null) {
    super(message);
    this.name = "AttachmentApiError";
    this.status = status;
    this.errorCode = errorCode;
  }
}
const SAFE_FALLBACK_FILENAME = "attachment";
const SAFE_NETWORK_MESSAGE = "Network error. Please try again.";

const NETWORK_ERROR_MESSAGE = "Unable to connect to the server. Please check your connection and try again.";
const FALLBACK_ERROR_MESSAGES: Record<number, string> = {
  400: "The request was invalid.",
  401: "You are not authenticated. Please sign in again.",
  403: "You are not allowed to perform this action.",
  404: "The requested item was not found.",
  410: "This attachment is no longer available.",
  413: "The file exceeds the 10 MB maximum attachment size.",
  415: "This file type is not supported.",
  500: "The server encountered an error. Please try again.",
};
function fallbackMessageForStatus(status: number): string {
  if (FALLBACK_ERROR_MESSAGES[status]) {
    return FALLBACK_ERROR_MESSAGES[status];
  }
  if (status >= 500) {
    return "The server encountered an error. Please try again.";
  }
  if (status >= 400) {
    return "The request could not be completed.";
  }
  return SAFE_NETWORK_MESSAGE;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
function readErrorEnvelope(
  body: unknown,
  status: number,
): { message: string; errorCode: string | null } {
  if (isRecord(body) && isRecord(body.detail)) {
    const detail = body.detail;
    const code =
      typeof detail.error_code === "string" && detail.error_code.length > 0
        ? detail.error_code
        : null;
    const msg =
      typeof detail.message === "string" && detail.message.length > 0
        ? detail.message
        : null;
    if (msg) {
      return { message: msg, errorCode: code };
    }
  }
  if (
    isRecord(body) &&
    typeof body.message === "string" &&
    body.message.length > 0
  ) {
    return { message: body.message, errorCode: null };
  }
  if (typeof body === "string" && body.trim().length > 0) {
    return { message: body, errorCode: null };
  }
  return { message: fallbackMessageForStatus(status), errorCode: null };
}
async function safeReadJson(response: Response): Promise<unknown | null> {
  if (response.status === 204) {
    return null;
  }
  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.toLowerCase().includes("application/json")) {
    return null;
  }
  const text = await response.text();
  if (text.length === 0) {
    return null;
  }
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

async function buildAttachmentError(
  response: Response,
  fallback: string,
): Promise<AttachmentApiError> {
  const body = await safeReadJson(response);
  const envelope = readErrorEnvelope(body, response.status);
  const message = envelope.message || fallback || fallbackMessageForStatus(response.status);
  return new AttachmentApiError(message, response.status, envelope.errorCode);
}

function runAttachmentRequest<T>(promise: Promise<T>): Promise<T> {
  return promise.catch((error: unknown) => {
    if (error instanceof AttachmentApiError) {
      throw error;
    }
    throw new AttachmentApiError(NETWORK_ERROR_MESSAGE, null, null);
  });
}
function sanitizeFilename(name: string): string | null {
  const filtered = name
    .split("")
    .filter((character) => {
      const code = character.charCodeAt(0);
      return code >= 32 && code !== 127;
    })
    .join("");
  const cleaned = filtered.replace(/[\\/]/g, "_").trim();
  if (cleaned.length === 0) {
    return null;
  }
  if (cleaned === "." || cleaned === "..") {
    return null;
  }
  return cleaned;
}
function decodePercentEncoded(input: string): string {
  try {
    return decodeURIComponent(input);
  } catch {
    const replaced: string[] = [];
    for (let i = 0; i < input.length; i++) {
      const ch = input[i];
      if (ch === "%") {
        const slice = input.slice(i, i + 3);
        if (/^%[0-9A-Fa-f]{2}$/.test(slice)) {
          replaced.push(slice);
          i += 2;
        } else {
          replaced.push("%");
        }
      } else {
        replaced.push(ch);
      }
    }
    try {
      return decodeURIComponent(replaced.join(""));
    } catch {
      return replaced.join("");
    }
  }
}
function extractContentDispositionFilename(
  header: string | null,
): string | null {
  if (!header) {
    return null;
  }
  const lowered = header.toLowerCase();
  const extIndex = lowered.indexOf("filename*=");
  if (extIndex !== -1) {
    const extStart = extIndex + "filename*=".length;
    let value = header.slice(extStart).split(";")[0].trim();
    const langMatch = value.match(/^([^']+)''/i);
    if (langMatch) {
      value = value.slice(langMatch[0].length);
    }
    value = value.replace(/^"|"$/g, "");
    if (value.length > 0) {
      const decoded = decodePercentEncoded(value);
      const sanitized = sanitizeFilename(decoded);
      if (sanitized) {
        return sanitized;
      }
    }
  }
  const plainIndex = lowered.indexOf("filename=");
  if (plainIndex !== -1) {
    const plainStart = plainIndex + "filename=".length;
    let value = header.slice(plainStart).split(";")[0].trim();
    value = value.replace(/^"|"$/g, "");
    const sanitized = sanitizeFilename(value);
    if (sanitized) {
      return sanitized;
    }
  }
  return null;
}

function buildSuggestedFilename(
  contentDisposition: string | null,
  fallback: string,
): string {
  const fromHeader = extractContentDispositionFilename(contentDisposition);
  if (fromHeader) {
    return fromHeader;
  }
  const sanitizedFallback = sanitizeFilename(fallback);
  if (sanitizedFallback) {
    return sanitizedFallback;
  }
  return SAFE_FALLBACK_FILENAME;
}
export function listTicketAttachments(
  ticketId: number,
): Promise<TicketAttachment[]> {
  return runAttachmentRequest(ticketingFetch(`/tickets/${ticketId}/attachments`).then(async (response) => {
    if (!response.ok) {
      throw await buildAttachmentError(
        response,
        fallbackMessageForStatus(response.status),
      );
    }
    const body = await safeReadJson(response);
    if (!Array.isArray(body)) {
      throw new AttachmentApiError(
        fallbackMessageForStatus(response.status),
        response.status,
        null,
      );
    }
    return body as TicketAttachment[];
  }));
}
export function uploadTicketAttachment(
  ticketId: number,
  file: File,
  isInternal: boolean = false,
): Promise<TicketAttachment> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("is_internal", isInternal ? "true" : "false");
  return runAttachmentRequest(ticketingFetch(`/tickets/${ticketId}/attachments`, {
    method: "POST",
    body: formData,
  }).then(async (response) => {
    if (!response.ok) {
      throw await buildAttachmentError(
        response,
        fallbackMessageForStatus(response.status),
      );
    }
    const body = await safeReadJson(response);
    if (!isRecord(body)) {
      throw new AttachmentApiError(
        fallbackMessageForStatus(response.status),
        response.status,
        null,
      );
    }
    return body as TicketAttachment;
  }));
}
export function downloadTicketAttachment(
  ticketId: number,
  attachmentId: number,
  fallbackFilename: string,
): Promise<AttachmentDownload> {
  return runAttachmentRequest(ticketingFetch(
    `/tickets/${ticketId}/attachments/${attachmentId}`,
  ).then(async (response) => {
    if (!response.ok) {
      throw await buildAttachmentError(
        response,
        fallbackMessageForStatus(response.status),
      );
    }
    const blob = await response.blob();
    const contentDisposition = response.headers.get("content-disposition");
    const contentType = response.headers.get("content-type");
    const filename = buildSuggestedFilename(
      contentDisposition,
      fallbackFilename,
    );
    return {
      blob,
      filename,
      contentType: contentType && contentType.length > 0 ? contentType : null,
    };
  }));
}
export function deleteTicketAttachment(
  ticketId: number,
  attachmentId: number,
): Promise<void> {
  return runAttachmentRequest(ticketingFetch(
    `/tickets/${ticketId}/attachments/${attachmentId}`,
    {
      method: "DELETE",
    },
  ).then(async (response) => {
    if (response.status === 204) {
      return;
    }
    if (!response.ok) {
      throw await buildAttachmentError(
        response,
        fallbackMessageForStatus(response.status),
      );
    }
  }));
}
